// roleAttention — per-role "things to address" engine. Given a role + user id,
// returns the actionable items that role needs to handle right now, each with a
// live count and a deep link. Powers the home "Needs your attention" list and
// the running to-address count. Jun 17, 2026.
//
// Each role's rule is a best-first cut — easy to tune (one block per role).
// Owned-deal counts use the viewer's id; approval/queue counts are company-wide.

import { supabase } from "../../lib/supabase";
import { PURSUABLE_STAGES } from "../../lib/playScope";
import { CANVASABLE_ROLES, CONFIRM_DOWS, dowOf, minutesTo10, todayET } from "../../lib/dailyCommitments";
import { fetchSpiffsWaitingCount } from "../crew/crewQueries";

/** Tue–Fri: people whose day the TL/ASM hasn't confirmed yet (Oct 2, 2026). */
async function dailyPlansItem(): Promise<AttentionItem | null> {
  const day = todayET();
  if (!CONFIRM_DOWS.has(dowOf(day))) return null;
  const [roster, done] = await Promise.all([
    head(supabase.from("team_roster").select("id", { count: "exact", head: true }).eq("is_active", true).in("role", [...CANVASABLE_ROLES])),
    head(supabase.from("daily_commitments").select("id", { count: "exact", head: true }).eq("day", day)),
  ]);
  const open = Math.max(0, roster - done);
  if (!open) return null;
  const mins = minutesTo10(day);
  return {
    tone: mins > 0 ? "amber" : "rose", icon: "✅", t1: "Daily plans to confirm",
    t2: mins > 0 ? `Due by 10:00 — ${mins} min left` : "Past 10:00 — these are late",
    count: open, to: "/canvas/manager-audit?mode=today",
  };
}

export interface AttentionItem {
  tone: "rose" | "amber" | "teal";
  icon: string;
  t1: string;
  t2: string;
  count: number;
  to: string;
}

const head = (q: PromiseLike<{ count: number | null }>) => q.then((r) => r.count ?? 0);

const isoD = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;

function weekBoundsISO(): [string, string] {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const we = new Date(d); we.setDate(we.getDate() + 6);
  return [`${isoD(d)}T00:00:00Z`, `${isoD(we)}T23:59:59Z`];
}

function tomorrowBoundsISO(): [string, string] {
  const t = new Date(); t.setHours(0, 0, 0, 0); t.setDate(t.getDate() + 1);
  return [`${isoD(t)}T00:00:00Z`, `${isoD(t)}T23:59:59Z`];
}

function todayBoundsISO(): [string, string] {
  const day = isoD(new Date());
  return [`${day}T00:00:00Z`, `${day}T23:59:59Z`];
}

export async function attentionFor(role: string | null | undefined, userId: string): Promise<AttentionItem[]> {
  const out: AttentionItem[] = [];
  const D = () => supabase.from("deals").select("id", { count: "exact", head: true });
  const own = `owner_id.eq.${userId},split_rm_id.eq.${userId}`;

  // A pursuable lead sitting a week+ without a stage move is going cold.
  const staleCut = (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString(); })();

  switch (role) {
    case "restore_manager": {
      const [unsigned, revise] = await Promise.all([
        head(D().eq("agreement_status", "approved").or(own)),
        head(D().eq("agreement_status", "needs_revision").or(own)),
      ]);
      if (unsigned) out.push({ tone: "rose", icon: "✍️", t1: "Agreements out for signature", t2: "Approved — follow up with the homeowner", count: unsigned, to: "/my-pipeline" });
      if (revise) out.push({ tone: "amber", icon: "📝", t1: "Agreements to revise", t2: "Changes requested — fix and resubmit", count: revise, to: "/my-pipeline" });
      break;
    }
    // Aug 24, 2026 (Ship 6): team_lead split from restore_manager — a TL now
    // gets TEAM-level signal (their RMs' agreements + idle pursuables), not
    // just their own two items. Team = roster rows whose team_lead_id is them.
    case "team_lead": {
      const { data: teamRows } = await supabase
        .from("team_roster").select("id").eq("is_active", true).eq("team_lead_id", userId);
      const teamIds = (teamRows ?? []).map((r: { id: string }) => r.id);
      const teamOr = teamIds.length
        ? `owner_id.in.(${teamIds.join(",")}),split_rm_id.in.(${teamIds.join(",")})`
        : null;
      const daily = await dailyPlansItem();
      if (daily) out.push(daily);
      const [unsigned, revise, teamUnsigned, teamIdle] = await Promise.all([
        head(D().eq("agreement_status", "approved").or(own)),
        head(D().eq("agreement_status", "needs_revision").or(own)),
        teamOr ? head(D().eq("agreement_status", "approved").or(teamOr)) : Promise.resolve(0),
        teamOr
          ? head(D().in("stage", PURSUABLE_STAGES as unknown as string[]).lte("stage_entered_at", staleCut).or(teamOr))
          : Promise.resolve(0),
      ]);
      if (unsigned) out.push({ tone: "rose", icon: "✍️", t1: "Agreements out for signature", t2: "Approved — follow up with the homeowner", count: unsigned, to: "/my-pipeline" });
      if (revise) out.push({ tone: "amber", icon: "📝", t1: "Agreements to revise", t2: "Changes requested — fix and resubmit", count: revise, to: "/my-pipeline" });
      if (teamUnsigned) out.push({ tone: "rose", icon: "👥", t1: "Team agreements out for signature", t2: "Your RMs' — nudge them to chase the ink", count: teamUnsigned, to: "/my-pipeline" });
      if (teamIdle) out.push({ tone: "amber", icon: "🥶", t1: "Team pursuables going cold", t2: "Leads idle 7+ days — coach them on it", count: teamIdle, to: "/run-the-play" });
      break;
    }
    case "asm": {
      const daily = await dailyPlansItem();
      if (daily) out.push(daily);
      const [ag, cv, dr, idle, sp] = await Promise.all([
        head(D().eq("agreement_status", "pending_approval")),
        head(supabase.from("pending_canvas_appointments").select("id", { count: "exact", head: true }).eq("status", "pending")),
        head(D().in("stage", ["pending_asm_review", "report_approved"])),
        head(D().in("stage", PURSUABLE_STAGES as unknown as string[]).lte("stage_entered_at", staleCut)),
        fetchSpiffsWaitingCount().catch(() => 0),
      ]);
      if (sp) out.push({ tone: "amber", icon: "💵", t1: "SPIFFs to approve", t2: "Approving sends them to Books for Tuesday", count: sp, to: "/asm?tab=spiffs" });
      if (ag) out.push({ tone: "rose", icon: "✅", t1: "Agreements to approve", t2: "First-gate review", count: ag, to: "/asm" });
      if (cv) out.push({ tone: "amber", icon: "🗺️", t1: "Canvasser appts to approve", t2: "Approving creates the deal", count: cv, to: "/asm" });
      if (dr) out.push({ tone: "teal", icon: "✈️", t1: "Drone intake to review", t2: "ASM gate", count: dr, to: "/asm" });
      if (idle) out.push({ tone: "amber", icon: "🥶", t1: "Pursuables going cold", t2: "Leads idle 7+ days across the sales team", count: idle, to: "/run-the-play" });
      break;
    }
    case "roof_specialist": {
      const [ws, we] = weekBoundsISO();
      const [mtg1, mtg2, preOpen] = await Promise.all([
        head(D().eq("assigned_rs_id", userId).gte("adjuster_meeting_date", ws).lte("adjuster_meeting_date", we)),
        head(D().eq("assigned_rs_id", userId).gte("reinspection_adjuster_meeting_date", ws).lte("reinspection_adjuster_meeting_date", we)),
        head(D().eq("pre_inspection_assigned_to", userId).eq("pre_inspection_completed", false)),
      ]);
      const mtg = mtg1 + mtg2;
      if (mtg) out.push({ tone: "teal", icon: "🏠", t1: "Adjuster meetings this week", t2: "You're the assigned specialist", count: mtg, to: "/dispatch" });
      if (preOpen) out.push({ tone: "amber", icon: "🪜", t1: "Pre-inspections to do", t2: "Assigned and still open", count: preOpen, to: "/dispatch" });
      break;
    }
    case "drone_pilot": {
      const n = await head(D().eq("assigned_drone_pilot_id", userId).eq("stage", "drone_scheduled"));
      if (n) out.push({ tone: "teal", icon: "✈️", t1: "Flights not done yet", t2: "Scheduled and not yet flown", count: n, to: "/drone" });
      break;
    }
    case "report_specialist": {
      const n = await head(D().eq("stage", "drone_complete"));
      if (n) out.push({ tone: "amber", icon: "📄", t1: "Drone reports to produce", t2: "Flown — awaiting the report", count: n, to: "/drone" });
      break;
    }
    case "qc": {
      const n = await head(D().eq("stage", "qc_review"));
      if (n) out.push({ tone: "amber", icon: "🔎", t1: "Reports in the QC queue", t2: "Awaiting your review", count: n, to: "/qc" });
      break;
    }
    case "roof_team_manager": {
      const [ts, te] = tomorrowBoundsISO();
      const [toApprove, builds, preOpen, mtgTom1, mtgTom2, opsOpen] = await Promise.all([
        // Builds submitted and waiting for approval — Robert approves too (Oct 22, 2026).
        head(D().eq("stage", "build_submitted")),
        head(D().eq("stage", "schedule_build").is("build_scheduled_date", null)),
        head(D().eq("stage", "pre_inspection_ready").eq("pre_inspection_completed", false)),
        head(D().gte("adjuster_meeting_date", ts).lte("adjuster_meeting_date", te)),
        head(D().gte("reinspection_adjuster_meeting_date", ts).lte("reinspection_adjuster_meeting_date", te)),
        // Ops Requests (Sep 25, 2026): what the sales team has asked him for.
        head(supabase.from("ops_requests").select("id", { count: "exact", head: true })
          .eq("assigned_to", userId).in("status", ["new", "in_progress", "waiting"])),
      ]);
      const mtgTom = mtgTom1 + mtgTom2;
      if (opsOpen) out.push({ tone: "amber", icon: "🛠", t1: "Ops requests waiting on you", t2: "Filed by the sales team", count: opsOpen, to: "/requests" });
      if (toApprove) out.push({ tone: "amber", icon: "🔨", t1: "Builds to approve", t2: "Submitted — approve on the Build Approvals board", count: toApprove, to: "/dispatch?view=robert&tab=approvals" });
      if (builds) out.push({ tone: "amber", icon: "📋", t1: "Builds to schedule", t2: "Approved — need a calendar date", count: builds, to: "/dispatch" });
      if (preOpen) out.push({ tone: "amber", icon: "🪜", t1: "Open pre-inspections", t2: "Pending across the team", count: preOpen, to: "/dispatch" });
      if (mtgTom) out.push({ tone: "teal", icon: "🏠", t1: "Adjuster meetings tomorrow", t2: "Make sure they're covered", count: mtgTom, to: "/dispatch" });
      break;
    }
    case "finance": {
      const [ar, comm] = await Promise.all([
        head(D().in("stage", ["collecting", "delinquent"])),
        head(D().eq("commission_approved", true).eq("commission_paid", false)),
      ]);
      if (ar) out.push({ tone: "rose", icon: "💵", t1: "Receivables to work", t2: "Collecting / delinquent", count: ar, to: "/finance" });
      if (comm) out.push({ tone: "amber", icon: "🧾", t1: "Approved commissions to process", t2: "Approved — mark paid / push to QB", count: comm, to: "/finance" });
      break;
    }
    // Leadership / company-overview pulse.
    case "admin":
    case "sales_director":
    case "director_of_operations":
    case "roof_director": {
      const [ts, te] = todayBoundsISO();
      const todayStr = isoD(new Date());
      const [unsigned, mtgToday1, mtgToday2, buildsToday] = await Promise.all([
        head(D().eq("agreement_status", "approved")),
        head(D().gte("adjuster_meeting_date", ts).lte("adjuster_meeting_date", te)),
        head(D().gte("reinspection_adjuster_meeting_date", ts).lte("reinspection_adjuster_meeting_date", te)),
        head(D().eq("build_scheduled_date", todayStr)),
      ]);
      const mtgToday = mtgToday1 + mtgToday2;
      if (unsigned) out.push({ tone: "rose", icon: "✍️", t1: "Approved agreements not signed", t2: "Sent — waiting on the homeowner", count: unsigned, to: "/my-pipeline" });
      if (mtgToday) out.push({ tone: "teal", icon: "🏠", t1: "Adjuster meetings today", t2: "Across the company", count: mtgToday, to: "/dispatch" });
      if (buildsToday) out.push({ tone: "amber", icon: "🔨", t1: "Builds today", t2: "On the calendar", count: buildsToday, to: "/dispatch" });
      break;
    }
    default:
      break;
  }
  return out;
}
